import React, { useState } from "react";
import { Container, Row, Col, Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  FaTasks,
  FaProjectDiagram,
  FaCheckCircle,
  FaSyncAlt,
  FaLaptopCode,
  FaBullhorn,
  FaCheck,
  FaArrowRight,
  FaLayerGroup,
} from "react-icons/fa";

// `Icon` is the component itself so it can be rendered at card size and
// modal size without cloneElement.
const SERVICES = [
  {
    title: "Project Management",
    Icon: FaTasks,
    description:
      "Project planning, governance, stakeholder management, risk control, budgeting, and successful delivery of strategic initiatives.",
    detail:
      "We help organizations plan, execute, and deliver projects on time, within budget, and aligned to business goals. From initiation to closure, our project managers bring proven governance, disciplined risk control, and clear stakeholder communication to keep every initiative on track.",
    offerings: [
      "End-to-end project planning & scheduling",
      "Governance, budgeting & cost control",
      "Risk, issue & dependency management",
      "Stakeholder engagement & reporting",
      "Agile, Waterfall & hybrid delivery",
    ],
  },
  {
    title: "Programme Management",
    Icon: FaProjectDiagram,
    description:
      "Leading large-scale transformation programmes, portfolio governance, benefits realization, and executive reporting.",
    detail:
      "For complex, multi-project initiatives, we provide programme leadership that coordinates interdependent workstreams toward a single strategic outcome. We align delivery with business benefits and give executives the visibility they need to make confident decisions.",
    offerings: [
      "Large-scale transformation programmes",
      "Portfolio & programme governance",
      "Benefits realization & tracking",
      "Cross-workstream coordination",
      "Executive & board-level reporting",
    ],
  },
  {
    title: "Test Management",
    Icon: FaCheckCircle,
    description:
      "Test strategy, quality assurance, UAT management, defect tracking, and end-to-end testing governance.",
    detail:
      "Quality is built in, not bolted on. Our test management services define the right strategy, coordinate testing across teams, and drive defects to closure — so you release with confidence and protect your users and reputation.",
    offerings: [
      "Test strategy & planning",
      "Functional, regression & UAT management",
      "Defect tracking & resolution governance",
      "Automation strategy & tooling",
      "Quality metrics & release readiness",
    ],
  },
  {
    title: "Digital Transformation",
    Icon: FaSyncAlt,
    description:
      "Helping organizations modernize operations, improve customer experiences, and accelerate business transformation.",
    detail:
      "We help you rethink how technology powers your business — modernizing legacy systems, streamlining operations, and reimagining customer journeys. Our approach balances quick wins with a sustainable roadmap for long-term change.",
    offerings: [
      "Digital strategy & roadmapping",
      "Legacy modernization & cloud migration",
      "Process automation & optimization",
      "Customer experience redesign",
      "Change management & adoption",
    ],
  },
  {
    title: "Website Development",
    Icon: FaLaptopCode,
    description:
      "Designing and developing responsive, scalable, and business-focused websites and digital platforms.",
    detail:
      "From marketing sites to complex web applications, we design and build fast, secure, and responsive digital platforms. Every build is optimized for performance, SEO, and conversion — and crafted to scale with your business.",
    offerings: [
      "Responsive, mobile-first design",
      "Modern front-end (React) development",
      "Scalable APIs & backend integration",
      "Performance, SEO & accessibility",
      "Ongoing support & maintenance",
    ],
  },
  {
    title: "Digital Marketing",
    Icon: FaBullhorn,
    description:
      "SEO, content marketing, social media management, campaign execution, and digital growth strategies.",
    detail:
      "We help you reach the right audience and turn attention into growth. Through data-driven SEO, content, and campaign management, we build a digital presence that generates leads and measurable business results.",
    offerings: [
      "Search engine optimization (SEO)",
      "Content & social media marketing",
      "Paid campaigns & lead generation",
      "Analytics & performance tracking",
      "Growth strategy & brand building",
    ],
  },
];

function ServiceCard({ service, index, onOpen }) {
  const { title, Icon, description } = service;

  return (
    <Col xl={4} md={6} data-aos="fade-up" data-aos-delay={(index % 3) * 90}>
      {/* The whole card is the control, so "Learn more" is a span rather
          than a nested button — one interactive element, one tab stop. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen(service)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(service);
          }
        }}
        className="svc-card h-100"
        data-live
        aria-label={`${title} — read more`}
      >
        <span className="svc-card__bar" />
        <span className="svc-card__num">{String(index + 1).padStart(2, "0")}</span>

        <span className="svc-card__icon">
          <Icon size={30} />
        </span>

        <h3 className="svc-card__title">{title}</h3>
        <p className="svc-card__text">{description}</p>

        <span className="svc-card__more">
          Learn more <FaArrowRight />
        </span>
      </div>
    </Col>
  );
}

function ServicesSection() {
  const [selected, setSelected] = useState(null);
  const handleClose = () => setSelected(null);
  const SelectedIcon = selected?.Icon;

  return (
    <section className="svc">
      <style>{`
        .svc {
          /* One peach family for the whole section. --peach-ink is the only
             tone dark enough to clear WCAG AA as text on white (5.2:1);
             --peach itself is 2.8:1 and is used for fills only. */
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;

          position: relative;
          overflow: hidden;
          padding: clamp(56px, 8vw, 96px) 0;
          background: linear-gradient(to bottom, #fff8f5 0%, #ffffff 55%, #fff6f1 100%);
        }
        .svc__blob {
          position: absolute; border-radius: 50%; pointer-events: none;
          filter: blur(90px); opacity: .55;
        }
        .svc__blob--a {
          width: 26rem; height: 26rem; top: -9rem; right: -9rem;
          background: radial-gradient(circle, #ffd0bb, transparent 70%);
        }
        .svc__blob--b {
          width: 22rem; height: 22rem; bottom: -8rem; left: -8rem;
          background: radial-gradient(circle, #ffe0cc, transparent 70%);
        }

        .svc__badge {
          display: inline-flex; align-items: center; gap: 9px;
          background: var(--peach-tint); color: var(--peach-ink);
          padding: 9px 22px; border-radius: 999px;
          font-weight: 700; font-size: .82rem; letter-spacing: .08em;
        }
        .svc__title {
          color: #0f172a; font-weight: 800;
          font-size: clamp(1.75rem, 4vw, 3.25rem);
          line-height: 1.15; letter-spacing: -.02em;
          margin-top: 22px;
        }
        /* Below sm the forced break makes lines wider than the viewport */
        .svc__br { display: none; }
        @media (min-width: 576px) { .svc__br { display: inline; } }

        .svc__lead {
          max-width: 750px; margin: 14px auto 0;
          color: #64748b; font-size: clamp(.96rem, 1.6vw, 1.06rem);
          line-height: 1.85;
        }

        .svc-card {
          position: relative;
          display: flex; flex-direction: column;
          padding: 30px 26px 26px;
          border-radius: 22px;
          background: #fff;
          border: 1px solid #eef2f7;
          box-shadow: 0 6px 22px rgba(15,23,42,.06);
          cursor: pointer; overflow: hidden;
          transition: transform .26s ease, box-shadow .26s ease, border-color .26s ease;
        }
        .svc-card:focus-visible {
          outline: 3px solid var(--peach-deep); outline-offset: 3px;
        }
        /* Peach rail that sweeps across the top edge on hover */
        .svc-card__bar {
          position: absolute; top: 0; left: 0; height: 4px; width: 100%;
          background: linear-gradient(90deg, var(--peach-deep), var(--peach-soft));
          transform: scaleX(0); transform-origin: left;
          transition: transform .32s cubic-bezier(.22,1,.36,1);
        }
        .svc-card__num {
          position: absolute; top: 16px; right: 22px;
          font-size: 2.5rem; font-weight: 800; line-height: 1;
          color: var(--peach); opacity: .16;
          transition: opacity .26s ease;
        }
        .svc-card__icon {
          width: 64px; height: 64px; border-radius: 18px;
          display: grid; place-items: center; margin-bottom: 20px;
          color: var(--peach-deep);
          background: var(--peach-tint);
          transition: background .26s ease, color .26s ease, transform .26s ease;
        }
        .svc-card__title {
          color: #0f172a; font-weight: 700;
          font-size: 1.2rem; margin-bottom: 10px;
        }
        .svc-card__text {
          color: #64748b; line-height: 1.75; font-size: .95rem;
          margin-bottom: 20px;
        }
        .svc-card__more {
          margin-top: auto;
          display: inline-flex; align-items: center; gap: 8px;
          color: var(--peach-ink); font-weight: 700; font-size: .92rem;
        }
        .svc-card__more svg { transition: transform .26s ease; }

        /* Two triggers for one look: a mouse hovers, a finger can't — so on
           touch, lib/touchMotion.js adds .is-live as the card scrolls through
           the middle of the screen. */
        .svc-card:hover,
        .svc-card.is-live {
          transform: translateY(-8px);
          box-shadow: 0 22px 46px rgba(194,65,12,.16);
          border-color: var(--peach-soft);
        }
        .svc-card:hover .svc-card__bar,
        .svc-card.is-live .svc-card__bar { transform: scaleX(1); }
        .svc-card:hover .svc-card__num,
        .svc-card.is-live .svc-card__num { opacity: .3; }
        .svc-card:hover .svc-card__icon,
        .svc-card.is-live .svc-card__icon {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #fff; transform: scale(1.06);
        }
        .svc-card:hover .svc-card__more svg,
        .svc-card.is-live .svc-card__more svg { transform: translateX(5px); }

        /* The modal renders in a portal outside .svc, so it can't inherit
           the tokens above — it carries its own copy of the peach values. */
        .svc-modal__head {
          border: none; padding: 22px 24px;
          background: linear-gradient(135deg, #ff6b35, #f3c09f);
        }
        .svc-modal__icon {
          width: 52px; height: 52px; border-radius: 15px;
          background: #fff; color: #ff6b35;
          display: inline-grid; place-items: center; flex-shrink: 0;
        }
        .svc-modal__tick {
          min-width: 22px; height: 22px; border-radius: 50%;
          background: #ffe9e0; color: #c2410c;
          display: inline-grid; place-items: center;
          font-size: .68rem; margin-top: 3px;
        }
        .svc-modal__cta {
          background: linear-gradient(135deg, #ff6b35, #ff9066);
          border: none; border-radius: 10px; color: #0f172a;
        }

        @media (prefers-reduced-motion: reduce) {
          .svc-card, .svc-card__bar, .svc-card__icon,
          .svc-card__num, .svc-card__more svg { transition: none; }
          .svc-card:hover { transform: none; }
        }
      `}</style>

      <span className="svc__blob svc__blob--a" />
      <span className="svc__blob svc__blob--b" />

      <Container style={{ position: "relative", zIndex: 1 }}>
        <div className="text-center mb-5" data-aos="fade-up">
          <span className="svc__badge">
            <FaLayerGroup />
            WHAT WE OFFER
          </span>

          <h2 className="svc__title">
            Services That Drive
            <br className="svc__br" />{" "}
            Business Success
          </h2>

          <p className="svc__lead">
            Supporting organizations through project delivery, enterprise
            technology solutions, digital transformation, and business growth
            initiatives.
          </p>
        </div>

        {/* g-4 gutters keep spacing even in both axes as columns reflow */}
        <Row className="g-4">
          {SERVICES.map((service, index) => (
            <ServiceCard
              key={service.title}
              service={service}
              index={index}
              onOpen={setSelected}
            />
          ))}
        </Row>
      </Container>

      {/* Service detail modal — full-screen on phones, where a centred
          dialog would leave the offerings list cramped and scrolling. */}
      <Modal
        show={!!selected}
        onHide={handleClose}
        centered
        size="lg"
        fullscreen="sm-down"
        scrollable
      >
        {selected && (
          <>
            <Modal.Header closeButton className="svc-modal__head">
              <Modal.Title
                className="d-flex align-items-center gap-3 fw-bold"
                style={{ color: "#ffffff", fontSize: "clamp(1.1rem,3vw,1.5rem)" }}
              >
                <span className="svc-modal__icon">
                  <SelectedIcon size={26} />
                </span>
                {selected.title}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
              <p
                style={{
                  color: "#475569",
                  lineHeight: 1.9,
                  fontSize: "clamp(.96rem,2vw,1.05rem)",
                }}
              >
                {selected.detail}
              </p>

              <h6 className="fw-bold mt-4 mb-3" style={{ color: "#0f172a" }}>
                What we deliver
              </h6>

              <Row className="g-3">
                {selected.offerings.map((item) => (
                  <Col md={6} key={item}>
                    <div className="d-flex align-items-start gap-2">
                      <span className="svc-modal__tick">
                        <FaCheck />
                      </span>
                      <span style={{ color: "#475569" }}>{item}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </Modal.Body>

            <Modal.Footer style={{ border: "none" }}>
              <Button
                variant="light"
                onClick={handleClose}
                style={{ borderRadius: "10px" }}
              >
                Close
              </Button>
              {/* Link, not a raw href — an <a> would hard-reload the SPA */}
              <Button
                as={Link}
                to="/contact"
                onClick={handleClose}
                className="svc-modal__cta fw-bold d-inline-flex align-items-center gap-2"
              >
                Get in Touch
                <FaArrowRight />
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </section>
  );
}

export default ServicesSection;
